import { jsx, jsxs } from "react/jsx-runtime";
import { cva, css } from "@/styled-system/css";
import { Box, Text, Icon, List } from "@zillow/constellation-10";
import { IconArrowRightOutline as ForwardRef } from "../../node_modules/@zillow/constellation-icons/dist/react/outline/icon-arrow-right-outline.esm.js";
import { IconCheckmarkOutline as ForwardRef$3 } from "../../node_modules/@zillow/constellation-icons/dist/react/outline/icon-checkmark-outline.esm.js";
import { IconCircleFilled as ForwardRef$2 } from "../../node_modules/@zillow/constellation-icons/dist/react/filled/icon-circle-filled.esm.js";
import { IconCloseOutline as ForwardRef$1 } from "../../node_modules/@zillow/constellation-icons/dist/react/outline/icon-close-outline.esm.js";
import { STICKY_NAV_HEIGHT_PX } from "@zillow/zrs-cms";
import makeContentInteractive from "../../util/makeSpansInteractive.esm.js";
const MIN_COLUMNS = 2;
const MAX_COLUMNS = 4;
const BULLET_ICONS = {
  checkmark: ForwardRef$3,
  dot: ForwardRef$2,
  cross: ForwardRef$1,
  arrow: ForwardRef
};
const layoutRecipe = cva({
  base: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "loose",
    alignItems: "stretch"
  },
  variants: {
    columnCount: {
      two: {
        lg: {
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))"
        }
      },
      three: {
        lg: {
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))"
        }
      },
      four: {
        lg: {
          gridTemplateColumns: "repeat(4, minmax(0, 1fr))"
        }
      }
    }
  }
});
const columnRecipe = cva({
  base: {
    backgroundColor: "bg.neutral",
    borderStyle: "solid",
    borderWidth: "default",
    // 16px — obj.default is 12px; matches the rounded comparison cards.
    borderRadius: "400",
    padding: "loose",
    height: "100%"
  },
  variants: {
    borderColor: {
      none: {
        borderWidth: "0",
        borderColor: "transparent"
      },
      blue: {
        borderColor: "border.accent.brand.hero"
      },
      black: {
        borderColor: "black"
      },
      red: {
        borderColor: "border.feedback.critical.hero"
      },
      green: {
        borderColor: "border.feedback.success.hero"
      }
    }
  }
});
const headerRecipe = cva({
  base: {
    // Text renders a span, and sticky only applies to a block-level box.
    display: "block",
    // Opaque fill so list items scroll underneath rather than through the
    // header, and a bleed over the column's `loose` padding so nothing
    // shows in the gap beside or above it.
    backgroundColor: "bg.neutral",
    marginInline: "calc(token(spacing.loose) * -1)",
    marginBlockStart: "calc(token(spacing.loose) * -1)",
    paddingInline: "loose",
    paddingBlockStart: "loose",
    // Replaces the former margin so the gap below the header stays filled.
    paddingBlockEnd: "default",
    // The bleed paints over the column's rounded top corners, so follow the
    // same curve to keep them from squaring off.
    borderStartStartRadius: "400",
    borderStartEndRadius: "400"
  },
  variants: {
    variant: {
      negative: {
        color: "text.neutral"
      },
      positive: {
        color: "text.accent.brand.hero"
      }
    },
    sticky: {
      true: {
        position: "sticky",
        // Sits below the B2B main nav. The pixel value is set on the block root.
        top: "var(--comparison-sticky-top)",
        zIndex: 1
      }
    }
  }
});
const itemStyles = css.raw({
  display: "flex",
  alignItems: "flex-start",
  gap: "tighter",
  paddingBlock: "none",
  marginBlockEnd: "loose",
  "&:last-child": {
    marginBlockEnd: "none"
  }
});
const iconRecipe = cva({
  base: {
    flex: "0 0 auto"
  },
  variants: {
    variant: {
      negative: {
        color: "text.subtle"
      },
      positive: {
        color: "text.accent.brand.hero"
      }
    }
  }
});
const columnIconRecipe = cva({
  base: {
    flex: "0 0 auto"
  },
  variants: {
    borderColor: {
      none: {
        color: "text.neutral"
      },
      blue: {
        color: "border.accent.brand.hero"
      },
      black: {
        color: "black"
      },
      red: {
        color: "border.feedback.critical.hero"
      },
      green: {
        color: "border.feedback.success.hero"
      }
    }
  }
});
const matrixScrollStyles = css.raw({
  // Narrow screens scroll the matrix sideways, but a scroll container also
  // traps `position: sticky`, so the headers can only pin to the viewport
  // once the table fits without one.
  overflowX: "auto",
  lg: {
    overflowX: "visible"
  }
});
const matrixTableStyles = css({
  width: "100%",
  minWidth: "640px",
  // Collapsed borders belong to the table rather than the cell, so a stuck
  // header leaves its outline behind. Separate borders travel with the cell.
  borderCollapse: "separate",
  borderSpacing: "0",
  tableLayout: "fixed"
});
const matrixCellRecipe = cva({
  base: {
    backgroundColor: "bg.neutral",
    // The matrix reads as rows rather than cards, so it uses one brand rule
    // under each row and no vertical lines. Per-column border colors only
    // apply to the card layout.
    borderStyle: "solid",
    borderWidth: "0",
    borderBlockEndWidth: "default",
    borderColor: "border.accent.brand.hero",
    padding: "loose",
    textAlign: "left",
    verticalAlign: "top"
  },
  variants: {
    topic: {
      true: {
        width: "25%",
        color: "text.neutral"
      }
    },
    sticky: {
      true: {
        position: "sticky",
        // Sits below the B2B main nav. The pixel value is set on the block root.
        top: "var(--comparison-sticky-top)",
        zIndex: 1
      }
    }
  }
});
const matrixItemStyles = css.raw({
  display: "flex",
  alignItems: "flex-start",
  gap: "tighter"
});
const matrixHeaderTextRecipe = cva({
  base: {
    display: "block"
  },
  variants: {
    variant: {
      negative: {
        color: "text.neutral"
      },
      positive: {
        color: "text.accent.brand.hero"
      }
    }
  }
});
function normalizeItem(item) {
  return typeof item === "string" ? item : (item == null ? void 0 : item.content) ?? "";
}
function normalizeItems(items) {
  if (!items) return [];
  return items.map(normalizeItem).filter((content) => content.length > 0);
}
function getDefaultBorderColor(index) {
  return index === 0 ? "black" : "blue";
}
function getDefaultBulletType(index) {
  return index === 0 ? "cross" : "checkmark";
}
function getColumnVariant(bulletType) {
  return bulletType === "cross" ? "negative" : "positive";
}
function resolveColumns(attrs) {
  const columns = ((attrs == null ? void 0 : attrs.columns) ?? []).slice(0, MAX_COLUMNS);
  while (columns.length < MIN_COLUMNS) {
    columns.push({});
  }
  return columns.map((column, index) => ({
    ...column,
    borderColor: column.borderColor ?? getDefaultBorderColor(index),
    bulletType: column.bulletType ?? getDefaultBulletType(index)
  }));
}
function getColumnCountVariant(count) {
  if (count >= MAX_COLUMNS) return "four";
  if (count === 3) return "three";
  return "two";
}
function ComparisonColumn({
  variant,
  header,
  items,
  borderColor,
  bulletType,
  sticky
}) {
  const ItemIcon = bulletType === "none" ? null : BULLET_ICONS[bulletType];
  return /* @__PURE__ */ jsxs(Box, { "data-testid": "comparison-column", "data-border-color": borderColor, "data-bullet-type": bulletType, "data-variant": variant, css: columnRecipe.raw({
    borderColor
  }), children: [
    header ? /* @__PURE__ */ jsx(Text, { textStyle: "body-bold", "data-sticky": sticky ? "true" : "false", css: headerRecipe.raw({
      variant,
      sticky
    }), children: makeContentInteractive(header) }) : null,
    items.length > 0 ? /* @__PURE__ */ jsx(List.Root, { appearance: "none", children: items.map((item, itemIndex) => /* @__PURE__ */ jsxs(
      List.Item,
      {
        css: itemStyles,
        children: [
          ItemIcon ? /* @__PURE__ */ jsx(Icon, { size: "md", "aria-hidden": true, "data-icon-color": borderColor, css: columnIconRecipe.raw({
            borderColor
          }), children: /* @__PURE__ */ jsx(ItemIcon, {}) }) : null,
          /* @__PURE__ */ jsx(Text, { textStyle: "body", children: makeContentInteractive(item) })
        ]
      },
      itemIndex
    )) }) : null
  ] });
}
function ComparisonMatrix({
  columns,
  topics,
  sticky
}) {
  const rowCount = Math.max(topics.length, ...columns.map((column) => {
    var _a;
    return ((_a = column.items) == null ? void 0 : _a.length) ?? 0;
  }));
  return /* @__PURE__ */ jsx(Box, { css: matrixScrollStyles, children: /* @__PURE__ */ jsxs("table", { className: matrixTableStyles, children: [
    /* @__PURE__ */ jsx("thead", { children: /* @__PURE__ */ jsxs("tr", { children: [
      /* @__PURE__ */ jsx("td", { "aria-hidden": true, "data-sticky": sticky ? "true" : "false", className: matrixCellRecipe({
        topic: true,
        sticky
      }) }),
      columns.map((column, columnIndex) => {
        const variant = getColumnVariant(column.bulletType);
        return /* @__PURE__ */ jsx(
          "th",
          {
            scope: "col",
            "data-variant": variant,
            "data-sticky": sticky ? "true" : "false",
            className: matrixCellRecipe({
              sticky
            }),
            children: column.header ? /* @__PURE__ */ jsx(Text, { textStyle: "body-bold", css: matrixHeaderTextRecipe.raw({
              variant
            }), children: makeContentInteractive(column.header) }) : null
          },
          columnIndex
        );
      })
    ] }) }),
    /* @__PURE__ */ jsx("tbody", { children: Array.from({
      length: rowCount
    }, (_, rowIndex) => (
      // Rows are positionally paired across all columns.
      // eslint-disable-next-line react/no-array-index-key
      /* @__PURE__ */ jsxs("tr", { children: [
        /* @__PURE__ */ jsx("th", { scope: "row", "data-testid": "comparison-topic", className: matrixCellRecipe({
          topic: true
        }), children: /* @__PURE__ */ jsx(Text, { textStyle: "body-bold", children: makeContentInteractive(normalizeItem(topics[rowIndex])) }) }),
        columns.map((column, columnIndex) => {
          var _a;
          const {
            bulletType
          } = column;
          const ItemIcon = bulletType === "none" ? null : BULLET_ICONS[bulletType];
          const item = normalizeItem((_a = column.items) == null ? void 0 : _a[rowIndex]);
          const variant = getColumnVariant(bulletType);
          return /* @__PURE__ */ jsx(
            "td",
            {
              "data-bullet-type": bulletType,
              "data-variant": variant,
              className: matrixCellRecipe(),
              children: item ? /* @__PURE__ */ jsxs(Box, { css: matrixItemStyles, children: [
                ItemIcon ? /* @__PURE__ */ jsx(Icon, { size: "md", "aria-hidden": true, css: iconRecipe.raw({
                  variant
                }), children: /* @__PURE__ */ jsx(ItemIcon, {}) }) : null,
                /* @__PURE__ */ jsx(Text, { textStyle: "body", children: makeContentInteractive(item) })
              ] }) : null
            },
            columnIndex
          );
        })
      ] }, rowIndex)
    )) })
  ] }) });
}
const comparisonStickyStyle = {
  "--comparison-sticky-top": `${STICKY_NAV_HEIGHT_PX}px`
};
const BlockComparison = ({
  attrs,
  index
}) => {
  const columns = resolveColumns(attrs);
  const stickyHeaders = (attrs == null ? void 0 : attrs.stickyHeaders) !== false;
  if (attrs == null ? void 0 : attrs.showTopics) {
    return /* @__PURE__ */ jsx(Box, { id: attrs.id, "data-testid": "comparison", "data-sticky": stickyHeaders ? "true" : "false", style: stickyHeaders ? comparisonStickyStyle : void 0, children: /* @__PURE__ */ jsx(ComparisonMatrix, { columns, topics: attrs.topics ?? [], sticky: stickyHeaders }) }, index);
  }
  return /* @__PURE__ */ jsx(Box, { id: attrs == null ? void 0 : attrs.id, "data-testid": "comparison", "data-sticky": stickyHeaders ? "true" : "false", style: stickyHeaders ? comparisonStickyStyle : void 0, css: layoutRecipe.raw({
    columnCount: getColumnCountVariant(columns.length)
  }), children: columns.map((column, columnIndex) => /* @__PURE__ */ jsx(
    ComparisonColumn,
    {
      variant: getColumnVariant(column.bulletType),
      header: column.header,
      items: normalizeItems(column.items),
      borderColor: column.borderColor,
      bulletType: column.bulletType,
      sticky: stickyHeaders
    },
    columnIndex
  )) }, index);
};
export {
  BlockComparison as default
};
